/**
 * 資源長條圖（Resource Graph / Histogram）與 EVM S 曲線的座標計算（純函式，無 SVG 依賴）。
 */

// ---------------------------------------------------------------------------
// 座標軸刻度（Y 軸數值刻度、X 軸時間刻度、標籤防重疊）
// ---------------------------------------------------------------------------

/** 把數字捨入成「好看」的刻度值（1/2/5 × 10^n），演算法同 D3 的 `nice number`。 */
function niceNumber(value: number, round: boolean): number {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const fraction = value / 10 ** exponent;
  let niceFraction: number;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

export interface ValueTick {
  readonly value: number;
  /** 0（最小）至 1（niceMax）之間的比例，供換算成 y 座標。 */
  readonly ratio: number;
}

export interface ValueAxis {
  /** 軸的滿刻度值（≥ 資料最大值的「好看」數字）；圖表縮放應以此為分母。 */
  readonly niceMax: number;
  readonly ticks: readonly ValueTick[];
}

/** 刻度數量的硬上限，防止極端輸入（浮點誤差、超大範圍）產生失控的迴圈或畫面。 */
const MAX_VALUE_TICKS = 20;

/** `maxValue` 不可用時（NaN／Infinity／<= 0）的退化刻度：固定回傳單一個 0 刻度。 */
const ZERO_AXIS: ValueAxis = { niceMax: 0, ticks: [{ value: 0, ratio: 0 }] };

/** `niceNumber()` 算出的 niceMax／niceStep 因浮點下溢等原因不可用時的退化刻度：只畫頭尾兩點。 */
function degenerateTwoPointAxis(maxValue: number): ValueAxis {
  return { niceMax: maxValue, ticks: [{ value: 0, ratio: 0 }, { value: maxValue, ratio: 1 }] };
}

/**
 * 計算 Y 軸刻度（金額／百分比皆適用，純數值，不含格式化）。
 *
 * 退化行為（絕不產出 NaN／Infinity，也絕不回傳空刻度陣列）：
 * - `maxValue` 非有限值（`NaN`／`±Infinity`）或 `<= 0` → 回傳單一 0 刻度。
 * - `maxValue` 有限但極小（例如 `Number.MIN_VALUE`）導致 `niceNumber()` 內部
 *   `10 ** exponent` 浮點下溢為 0、算出的 niceStep/niceMax 不可用 → 退化成只有
 *   頭尾兩個刻度（0 與 maxValue 本身），不強行套用「好看數字」演算法。
 * - 刻度數量夾在 `MAX_VALUE_TICKS` 以內，避免極端 `maxValue`（如
 *   `Number.MAX_SAFE_INTEGER`）在浮點誤差下跑出遠超預期的刻度數。
 */
export function computeValueAxis(maxValue: number, targetTickCount = 4): ValueAxis {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return ZERO_AXIS;

  const niceRange = niceNumber(maxValue, false);
  const niceStep = niceNumber(niceRange / Math.max(1, targetTickCount - 1), true);
  if (!Number.isFinite(niceStep) || niceStep <= 0) return degenerateTwoPointAxis(maxValue);

  const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
  if (!Number.isFinite(niceMax) || niceMax <= 0) return degenerateTwoPointAxis(maxValue);

  const ticks: ValueTick[] = [];
  for (let v = 0; v <= niceMax + niceStep / 2 && ticks.length < MAX_VALUE_TICKS; v += niceStep) {
    ticks.push({ value: v, ratio: v / niceMax });
  }
  if (ticks.length === 0) return degenerateTwoPointAxis(maxValue);
  return { niceMax, ticks };
}

export interface TimeTick {
  readonly x: number;
  readonly ms: number;
}

const DAY_MS = 86_400_000;
/**
 * 「好看」的日期間距（天），依資料時間跨度挑選最接近目標刻度數的一個。
 * 上限延伸到 1000 年（365000 天）——原本停在 730 天（2 年）時，時間跨度一旦超過
 * 「目標刻度數 × 730 天」（例如寬版面、長跨度同時出現），級距就卡住不再放大，
 * 逐步逼近 `MAX_TIME_TICKS` 硬上限，使寬度驅動的密度計算在這個區間形同失效
 * （不管算出的目標刻度數多小，都會逼近上限、標籤彼此重疊）。延伸後級距能跟著
 * 跨度一路放大到年、十年、百年，維持刻度數貼近目標值。
 */
const NICE_DAY_STEPS: readonly number[] = [
  1, 2, 3, 5, 7, 10, 14, 21, 30, 45, 60, 90, 120, 180, 365, 730, 1825, 3650, 7300, 18250, 36500, 73000, 182500,
  365000,
];
/** 刻度數量的硬上限，防止極端輸入產生失控的迴圈或畫面。 */
const MAX_TIME_TICKS = 40;

/**
 * 計算 X 軸時間刻度：在 `[startMs, endMs]` 區間內，依「好看」的日期間距
 * （對齊到該間距的整數倍時間點）產生刻度，並線性映射到 `[0, width]`。
 *
 * 目標刻度數由**可用像素寬度 ÷ 標籤預估寬度**推導（`minLabelWidth`，預設 60px，
 * 約可容納 `MM-DD` 一類的短日期標籤），而非寫死的常數——容器越窄，刻度密度越低，
 * 避免標籤在小尺寸下彼此重疊。
 *
 * 退化行為：`startMs`／`endMs` 非有限值（例如上游 `Date.parse()` 對非法日期回傳的
 * `NaN`）、`endMs <= startMs`、或 `width <= 0` 時，一律回傳以有限值兜底的單一刻度，
 * 不產出 `NaN` 座標。
 */
export function computeTimeTicks(
  startMs: number,
  endMs: number,
  width: number,
  minLabelWidth = 60,
): readonly TimeTick[] {
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    !Number.isFinite(width) ||
    endMs <= startMs ||
    width <= 0
  ) {
    return [{ x: 0, ms: Number.isFinite(startMs) ? startMs : 0 }];
  }

  const targetTickCount = Math.max(2, Math.min(MAX_TIME_TICKS, Math.floor(width / Math.max(1, minLabelWidth))));

  const totalDays = (endMs - startMs) / DAY_MS;
  const roughStep = totalDays / Math.max(1, targetTickCount - 1);
  let stepDays = NICE_DAY_STEPS[NICE_DAY_STEPS.length - 1] as number;
  for (const step of NICE_DAY_STEPS) {
    if (step >= roughStep) {
      stepDays = step;
      break;
    }
  }
  const stepMs = stepDays * DAY_MS;

  const ticks: TimeTick[] = [];
  const alignedStart = Math.ceil(startMs / stepMs) * stepMs;
  for (let t = alignedStart; t <= endMs && ticks.length < MAX_TIME_TICKS; t += stepMs) {
    ticks.push({ x: ((t - startMs) / (endMs - startMs)) * width, ms: t });
  }
  if (ticks.length === 0) {
    ticks.push({ x: 0, ms: startMs }, { x: width, ms: endMs });
  }
  return ticks;
}

/**
 * 從 `count` 個等寬排列的項目中挑出「不會重疊」的標籤索引：
 * 依可用寬度反推每隔幾個項目才顯示一個標籤，並固定含頭尾。
 */
export function decimateIndices(count: number, width: number, minLabelWidth = 50): readonly number[] {
  if (count <= 0) return [];
  if (count === 1) return [0];
  const perItem = width / count;
  const step = Math.max(1, Math.ceil(minLabelWidth / Math.max(perItem, 1)));
  const indices: number[] = [];
  for (let i = 0; i < count; i += step) indices.push(i);
  const last = count - 1;
  if (indices[indices.length - 1] !== last) indices.push(last);
  return indices;
}

export interface HistogramPoint {
  readonly periodStart: string;
  readonly label: string;
  /** 該期間的分配量（單位：Units，1 = 100%）。 */
  readonly allocatedUnits: number;
}

export interface BarSegment {
  /** 藍色（未超過可用量）部分的高度。 */
  readonly normalHeight: number;
  /** 紅色（超過可用量）部分的高度。 */
  readonly overHeight: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly totalHeight: number;
}

export interface HistogramGeometry {
  readonly bars: readonly BarSegment[];
  /** 可用量參考線的 y 座標。 */
  readonly referenceLineY: number;
  readonly chartHeight: number;
  readonly chartWidth: number;
}

/**
 * 計算長條圖幾何：每個 bar 依可用量（maxUnits）切成「未超過」與「超過」兩段，
 * 頂端對齊資料實際值（超過時整個 bar 都比參考線高）。
 * `scaleMax` 決定滿版高度對應的數值；未提供時取「資料最大值與 maxUnits 的較大者」，
 * 並保留 10% 邊界，避免最高的長條頂到圖表邊緣。
 */
export function computeHistogramGeometry(
  points: readonly HistogramPoint[],
  maxUnits: number,
  width: number,
  height: number,
  scaleMax?: number,
): HistogramGeometry {
  const dataMax = points.reduce((m, p) => Math.max(m, p.allocatedUnits), 0);
  const ceiling = scaleMax ?? Math.max(dataMax, maxUnits, 0.0001) * 1.1;
  const scale = ceiling > 0 ? height / ceiling : 0;
  const referenceLineY = height - maxUnits * scale;

  const count = Math.max(points.length, 1);
  const gap = width / count / 5;
  const barWidth = Math.max(1, width / count - gap);

  const bars: BarSegment[] = points.map((point, index) => {
    const x = (width / count) * index + gap / 2;
    const value = Math.max(0, point.allocatedUnits);
    const totalHeight = value * scale;
    const normalValue = Math.min(value, maxUnits);
    const overValue = Math.max(0, value - maxUnits);
    const normalHeight = normalValue * scale;
    const overHeight = overValue * scale;
    const y = height - totalHeight;
    return { x, y, width: barWidth, normalHeight, overHeight, totalHeight };
  });

  return { bars, referenceLineY, chartHeight: height, chartWidth: width };
}

// ---------------------------------------------------------------------------
// S 曲線（EVM 報表沿用同一套座標計算）
// ---------------------------------------------------------------------------

export interface CurvePoint {
  readonly date: string;
  readonly pv: number;
  readonly ev: number;
  readonly ac: number;
}

export interface ScaledPoint {
  readonly x: number;
  readonly y: number;
}

export interface SCurveGeometry {
  readonly pv: readonly ScaledPoint[];
  readonly ev: readonly ScaledPoint[];
  readonly ac: readonly ScaledPoint[];
  /** 狀態日在圖上的 x 座標；資料為空或日期超出範圍時為 null。 */
  readonly statusDateX: number | null;
  readonly chartWidth: number;
  readonly chartHeight: number;
}

function toEpochMs(date: string): number {
  return Date.parse(date);
}

/**
 * 計算 S 曲線（PV／EV／AC 三條累積曲線）的座標，並定位狀態日的垂直線。
 * `valueMax` 未提供時取資料最大值（沿用舊行為）；提供時（例如搭配 `computeValueAxis()`
 * 算出的 `niceMax`）用來對齊 Y 軸刻度，避免曲線頂端與軸刻度的滿刻度值對不上。
 */
export function computeSCurveGeometry(
  points: readonly CurvePoint[],
  statusDate: string,
  width: number,
  height: number,
  valueMax?: number,
): SCurveGeometry {
  if (points.length === 0) {
    return { pv: [], ev: [], ac: [], statusDateX: null, chartWidth: width, chartHeight: height };
  }
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined) {
    return { pv: [], ev: [], ac: [], statusDateX: null, chartWidth: width, chartHeight: height };
  }

  const t0 = toEpochMs(first.date);
  const t1 = toEpochMs(last.date);
  const timeSpan = Math.max(1, t1 - t0);

  const dataMax = points.reduce((m, p) => Math.max(m, p.pv, p.ev, p.ac), 0);
  const maxValue = valueMax ?? dataMax;
  const valueScale = maxValue > 0 ? height / maxValue : 0;

  const xOf = (date: string): number => ((toEpochMs(date) - t0) / timeSpan) * width;
  const yOf = (value: number): number => height - value * valueScale;

  const pv = points.map((p) => ({ x: xOf(p.date), y: yOf(p.pv) }));
  const ev = points.map((p) => ({ x: xOf(p.date), y: yOf(p.ev) }));
  const ac = points.map((p) => ({ x: xOf(p.date), y: yOf(p.ac) }));

  const statusMs = toEpochMs(statusDate);
  const statusDateX = statusMs >= t0 && statusMs <= t1 ? xOf(statusDate) : null;

  return { pv, ev, ac, statusDateX, chartWidth: width, chartHeight: height };
}

export function toPolylinePoints(points: readonly ScaledPoint[]): string {
  return points.map((p) => `${p.x},${p.y}`).join(' ');
}
